import * as THREE from 'three'
import type { KanjinSite } from '../kanjinData'

export interface ChipLaunch {
  from: THREE.Vector3
  to: THREE.Vector3
  apex?: number
  dur?: number
  delay?: number
  site?: KanjinSite
  via?: THREE.Vector3
  scale?: number
}

interface Chip extends ChipLaunch {
  t: number
  active: boolean
  arrived: boolean
  p1: THREE.Vector3
}

const TRAIL_PER_CHIP = 14

/** 木片（Instanced）＋ベジェ弧＋加算トレイル */
export class WoodChips {
  readonly root = new THREE.Group()
  onArrive?: (site: KanjinSite | undefined, index: number) => void

  private mesh: THREE.InstancedMesh
  private chips: Chip[] = []
  private max: number
  private trail: THREE.Points
  private trailPos: Float32Array
  private trailCol: Float32Array
  private trailHead = 0
  private dummy = new THREE.Object3D()
  private tmp = new THREE.Vector3()
  private tmp2 = new THREE.Vector3()

  constructor(max = 240) {
    this.max = max
    const geo = new THREE.BoxGeometry(0.55, 0.09, 1.0)
    const mat = new THREE.MeshStandardMaterial({
      map: WoodChips.makeChipTexture(),
      color: 0xd8c396,
      roughness: 0.7,
      emissive: 0x7a5a20,
      emissiveIntensity: 0.35,
    })
    this.mesh = new THREE.InstancedMesh(geo, mat, max)
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage)
    this.mesh.count = 0
    this.mesh.frustumCulled = false
    this.root.add(this.mesh)

    const n = max * TRAIL_PER_CHIP
    this.trailPos = new Float32Array(n * 3)
    this.trailCol = new Float32Array(n * 3)
    const tgeo = new THREE.BufferGeometry()
    tgeo.setAttribute('position', new THREE.BufferAttribute(this.trailPos, 3))
    tgeo.setAttribute('color', new THREE.BufferAttribute(this.trailCol, 3))
    const tmat = new THREE.PointsMaterial({
      size: 0.9,
      vertexColors: true,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
    })
    this.trail = new THREE.Points(tgeo, tmat)
    this.trail.frustumCulled = false
    this.root.add(this.trail)
  }

  launch(o: ChipLaunch): number {
    let idx = this.chips.findIndex((c) => !c.active)
    const p1 =
      o.via?.clone() ??
      o.from.clone().add(o.to).multiplyScalar(0.5).add(new THREE.Vector3(0, o.apex ?? 20, 0))
    const chip: Chip = {
      ...o,
      dur: o.dur ?? 4,
      delay: o.delay ?? 0,
      scale: o.scale ?? 1,
      t: -(o.delay ?? 0),
      active: true,
      arrived: false,
      p1,
    }
    if (idx < 0) {
      if (this.chips.length >= this.max) return -1
      idx = this.chips.length
      this.chips.push(chip)
    } else {
      this.chips[idx] = chip
    }
    this.mesh.count = Math.max(this.mesh.count, this.chips.length)
    return idx
  }

  launchWave(
    starts: { pos: THREE.Vector3; site: KanjinSite }[],
    target: THREE.Vector3,
    opts: { stagger?: number; dur?: number; apexMin?: number; apexMax?: number } = {},
  ): void {
    starts.forEach((s, i) => {
      this.launch({
        from: s.pos,
        to: target,
        site: s.site,
        delay: i * (opts.stagger ?? 0.35),
        dur: opts.dur ?? 4.5,
        apex: (opts.apexMin ?? 18) + Math.random() * ((opts.apexMax ?? 30) - (opts.apexMin ?? 18)),
      })
    })
  }

  get activeCount(): number {
    return this.chips.filter((c) => c.active).length
  }

  clear(): void {
    this.chips = []
    this.mesh.count = 0
    this.trailCol.fill(0)
    ;(this.trail.geometry.getAttribute('color') as THREE.BufferAttribute).needsUpdate = true
  }

  /** 指定チップの現在位置（ニアミス検知等） */
  positionOf(index: number, out: THREE.Vector3): boolean {
    const c = this.chips[index]
    if (!c || !c.active || c.t < 0) return false
    this.evalBezier(c, Math.min(1, c.t / c.dur!), out)
    return true
  }

  private evalBezier(c: Chip, u: number, out: THREE.Vector3): void {
    const a = 1 - u
    out
      .copy(c.from)
      .multiplyScalar(a * a)
      .addScaledVector(c.p1, 2 * a * u)
      .addScaledVector(c.to, u * u)
  }

  update(dt: number): void {
    // トレイル減衰
    for (let i = 0; i < this.trailCol.length; i++) this.trailCol[i] *= Math.pow(0.06, dt)

    for (let i = 0; i < this.chips.length; i++) {
      const c = this.chips[i]
      if (!c.active) {
        this.dummy.position.set(0, -9999, 0)
        this.dummy.scale.setScalar(0.0001)
        this.dummy.updateMatrix()
        this.mesh.setMatrixAt(i, this.dummy.matrix)
        continue
      }
      c.t += dt
      const u = Math.min(1, Math.max(0, c.t / c.dur!))
      this.evalBezier(c, u, this.tmp)

      // 向き＝接線
      this.evalBezier(c, Math.min(1, u + 0.02), this.tmp2)
      this.dummy.position.copy(this.tmp)
      if (this.tmp2.distanceToSquared(this.tmp) > 1e-8) this.dummy.lookAt(this.tmp2)
      this.dummy.rotateZ(c.t * 2.2)
      this.dummy.scale.setScalar(c.scale!)
      this.dummy.updateMatrix()
      this.mesh.setMatrixAt(i, this.dummy.matrix)

      // トレイル書き込み
      if (c.t >= 0 && u < 1) {
        const slot = this.trailHead % (this.max * TRAIL_PER_CHIP)
        this.trailHead++
        this.trailPos[slot * 3] = this.tmp.x
        this.trailPos[slot * 3 + 1] = this.tmp.y
        this.trailPos[slot * 3 + 2] = this.tmp.z
        this.trailCol[slot * 3] = 1.0
        this.trailCol[slot * 3 + 1] = 0.78
        this.trailCol[slot * 3 + 2] = 0.35
      }

      if (u >= 1 && !c.arrived) {
        c.arrived = true
        c.active = false
        this.onArrive?.(c.site, i)
      }
    }
    this.mesh.instanceMatrix.needsUpdate = true
    ;(this.trail.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true
    ;(this.trail.geometry.getAttribute('color') as THREE.BufferAttribute).needsUpdate = true
  }

  static makeChipTexture(inkText?: string): THREE.CanvasTexture {
    const c = document.createElement('canvas')
    c.width = 256
    c.height = 256
    const ctx = c.getContext('2d')!
    ctx.fillStyle = '#d9c496'
    ctx.fillRect(0, 0, 256, 256)
    for (let i = 0; i < 26; i++) {
      ctx.strokeStyle = `rgba(122,90,48,${0.12 + Math.random() * 0.2})`
      ctx.lineWidth = 1 + Math.random() * 2
      const y = Math.random() * 256
      ctx.beginPath()
      ctx.moveTo(0, y)
      for (let x = 0; x <= 256; x += 16) ctx.lineTo(x, y + Math.sin(x * 0.05 + i * 2) * 3)
      ctx.stroke()
    }
    if (inkText) {
      ctx.fillStyle = 'rgba(30,24,16,0.88)'
      ctx.font = '600 44px "HGP行書体", "HG行書体", "Yu Mincho", serif'
      ctx.textAlign = 'center'
      const chars = inkText.split('')
      chars.forEach((ch, i) => ctx.fillText(ch, 128, 52 + i * 46))
    }
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    return tex
  }
}
